import React, {useEffect, useImperativeHandle, useRef, useState} from 'react';
import PropTypes from 'prop-types';

import {useClickOutside} from '../../lib/customHooks';
import {mergeRefs} from '../../lib/refHelper';

import './ContexMenu.scss';

function ContexMenuButton({onClick, hideContextMenu, text, glyphicon, disabled}) {
  return (
    <button
      type="button"
      className="item"
      disabled={disabled}
      onClick={() => {
        onClick();
        hideContextMenu();
      }}
    >
      <span className={glyphicon} />
      {text}
    </button>
  );
}

ContexMenuButton.propTypes = {
  glyphicon: PropTypes.string,
  text: PropTypes.string,
  disabled: PropTypes.bool,
  onClick: PropTypes.func,
  hideContextMenu: PropTypes.func,
};

ContexMenuButton.defaultProps = {
  glyphicon: '',
  text: '',
  disabled: false,
  onClick: () => {},
  hideContextMenu: () => {},
};

const ContextMenu = React.forwardRef(({children, hideContextMenu, isDisable, excludedComponentRef}, ref) => {
  const [show, setShow] = useState(false);
  const containerRef = useRef();
  const eventRef = useRef();
  const showTriggred = useRef();

  const onHideContextMenu = () => {
    eventRef.current = null;
    hideContextMenu();
    setShow(false);
  };

  useClickOutside(containerRef, onHideContextMenu, excludedComponentRef);

  const showContextMenu = () => {
    const evt = eventRef.current;
    const menuContext = containerRef.current;
    if (!menuContext || !evt) return;

    const divHeight = window.innerHeight;
    const divWidth = window.innerWidth;

    const {height: menuContextHeight, width: menuContextWidth} = menuContext.getBoundingClientRect();

    const xPos = evt.type === 'touchend' ? evt.changedTouches[0].clientX : evt.clientX;
    const yPos = evt.type === 'touchend' ? evt.changedTouches[0].clientY : evt.clientY;

    let clickX = xPos;
    let clickY = yPos;

    if (clickY + menuContextHeight > divHeight) {
      clickY -= menuContextHeight;
    }
    if (clickX + menuContextWidth > divWidth) {
      clickX -= menuContextWidth;
    }

    menuContext.style.top = `${clickY}px`;
    menuContext.style.left = `${clickX}px`;
  };

  useEffect(() => {
    if (show && containerRef.current) {
      showContextMenu();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showTriggred.current]);

  useImperativeHandle(ref, () => ({
    show(e) {
      if (!isDisable) {
        eventRef.current = e;
        showTriggred.current = Math.random();
        setShow(true);
      }
    },
    hide() {
      setShow(false);
    },
    isShow() {
      return show;
    },
    getContainerRef() {
      return containerRef;
    },
  }));

  return (
    <div id="context-menu" tabIndex="-1" ref={mergeRefs(ref, containerRef)} className={`${show ? '' : 'hide'}`}>
      {children}
    </div>
  );
});

ContextMenu.propTypes = {
  children: PropTypes.any.isRequired,
  hideContextMenu: PropTypes.func.isRequired,
  isDisable: PropTypes.bool,
  excludedComponentRef: PropTypes.array,
};

ContextMenu.defaultProps = {
  isDisable: false,
  excludedComponentRef: [],
};

ContextMenu.Button = ContexMenuButton;

export default ContextMenu;
